import axiosIns from "@/libs/axios";

export default {
  namespaced: true,
  state: {
    isLoggedIn: false,
    user: null,
    navMenuItems: [],
  },
  getters: {
    getLoggedInUser: (state) => {
      return state.user;
    },
    hasPermission: (state) => (name) => {
      if (state.user) {
        return state.user.permissions.hasOwnProperty(name);
      }
      return false;
    },
    hasRole: (state) => (name) => {
      if (state.user.role_data.code_name === name) {
        return true;
      }
      return false;
    },
    isLoggedIn: (state) => state.isLoggedIn,
    getNavMenuItems: (state) => state.navMenuItems,
  },
  mutations: {
    login(state, data) {
      state.isLoggedIn = true;
      state.user = data.user;
    },
    logout(state) {
      state.isLoggedIn = false;
      state.user = null;
    },
    setNavMenuItems(state) {
      state.navMenuItems = [];

      if (state.user.permissions.hasOwnProperty("show_user")) {
        state.navMenuItems.push({
          title: "User",
          icon: "UserIcon",
          route: "User",
        });
      }

      if (state.user.permissions.hasOwnProperty("show_role")) {
        state.navMenuItems.push({
          title: "Role",
          icon: "UsersIcon",
          route: "Role",
        });
      }

      if (state.user.permissions.hasOwnProperty("show_payment")) {
        state.navMenuItems.push({
          title: "Payment",
          icon: "FileIcon",
          route: "Payment",
        });
      }

      if (state.user.permissions.hasOwnProperty("show_config")) {
        state.navMenuItems.push({
          title: "Config",
          icon: "SettingsIcon",
          route: "Config",
        });
      }

      if (state.user.permissions.hasOwnProperty("show_server")) {
        state.navMenuItems.push({
          title: "Server",
          icon: "ShieldIcon",
          route: "Server",
        });
      }

      if (state.user.permissions.hasOwnProperty("show_country")) {
        state.navMenuItems.push({
          title: "Country",
          icon: "GlobeIcon",
          route: "Country",
        });
      }
    },
  },
  actions: {
    async login({ commit }, payload) {
      const res = await axiosIns.post("login", payload);
      if (res.status === 200) {
        commit("login", res.data);
        commit("setNavMenuItems");
      }
      return res;
    },
    async logout({ commit }) {
      const res = await axiosIns.post("logout");
      if (res.status === 204) {
        commit("logout");
      }
      return res;
    },
    async getCurrentUser({ commit }) {
      const res = await axiosIns.get("current");
      if (res.status === 200) {
        commit("login", res.data);
        commit("setNavMenuItems");
      }
    },
    async getRoles({ commit }, { pageNumber, name }) {
      let url = `roles/?page=${pageNumber}`;
      if (name) {
        url += `&name=${name}`;
      }
      const res = await axiosIns.get(url);
      return res;
    },
    async getRole({ commit }, { pk }) {
      const res = await axiosIns.get(`roles/${pk}/`);
      return res;
    },
    async createRole({ commit }, payload) {
      const res = await axiosIns.post("roles/", payload);
      return res;
    },
    async updateRole({ commit }, { payload, pk }) {
      const res = await axiosIns.put(`roles/${pk}/`, payload);
      return res;
    },
    async deleteRole({ commit }, { pk }) {
      const res = await axiosIns.delete(`roles/${pk}/`);
      return res;
    },
    async getPermissions({ commit }) {
      const res = await axiosIns.get("permissions");
      return res;
    },
    async getUsers(
      { commit },
      { pageNumber, name, username }
    ) {
      let url = `users/?page=${pageNumber}`;
      if (name) {
        url += `&name=${name}`;
      }
      if (username) {
        url += `&username=${username}`;
      }
      const res = await axiosIns.get(url);
      return res;
    },
    async getUser({ commit }, { pk }) {
      const res = await axiosIns.get(`users/${pk}/`);
      return res;
    },
    async createUser({ commit }, payload) {
      const res = await axiosIns.post("users/", payload);
      return res;
    },
    async updateUser({ commit }, { payload, pk }) {
      const res = await axiosIns.patch(`users/${pk}/`, payload);
      return res;
    },
    async deleteUser({ commit }, { pk }) {
      const res = await axiosIns.delete(`users/${pk}/`);
      return res;
    },
    async getPayments(
      { commit },
      { pageNumber, purchase_id, created_by_username }
    ) {
      let url = `payments/?page=${pageNumber}`;
      if (purchase_id) {
        url += `&purchase_id=${purchase_id}`;
      }
      if (created_by_username) {
        url += `&created_by_username=${created_by_username}`;
      }
      const res = await axiosIns.get(url);
      return res;
    },
    async getPayment({ commit }, { pk }) {
      const res = await axiosIns.get(`payments/${pk}/`);
      return res;
    },
    async createPayment({ commit }, payload) {
      const res = await axiosIns.post("payments/", payload);
      return res;
    },
    async updatePayment({ commit }, { payload, pk }) {
      const res = await axiosIns.patch(`payments/${pk}/`, payload);
      return res;
    },
    async deletePayment({ commit }, { pk }) {
      const res = await axiosIns.delete(`payments/${pk}/`);
      return res;
    },
    async getConfigs({ commit }, { pageNumber }) {
      let url = `configs/?page=${pageNumber}`;
      const res = await axiosIns.get(url);
      return res;
    },
    async getConfig({ commit }, { pk }) {
      const res = await axiosIns.get(`configs/${pk}/`);
      return res;
    },
    async createConfig({ commit }, payload) {
      const res = await axiosIns.post("configs/", payload);
      return res;
    },
    async updateConfig({ commit }, { payload, pk }) {
      const res = await axiosIns.patch(`configs/${pk}/`, payload);
      return res;
    },
    async deleteConfig({ commit }, { pk }) {
      const res = await axiosIns.delete(`configs/${pk}/`);
      return res;
    },
    async getCaptcha({ commit }, payload) {
      const res = await axiosIns.get("captcha/refresh/", {
        headers: {
          "x-requested-with": "XMLHttpRequest",
        },
      });
      return res;
    },
    async getServers({ commit }, { pageNumber }) {
      let url = `servers/?page=${pageNumber}`;
      const res = await axiosIns.get(url);
      return res;
    },
    async getServer({ commit }, { pk }) {
      const res = await axiosIns.get(`servers/${pk}/`);
      return res;
    },
    async createServer({ commit }, payload) {
      const res = await axiosIns.post("servers/", payload);
      return res;
    },
    async updateServer({ commit }, { payload, pk }) {
      const res = await axiosIns.patch(`servers/${pk}/`, payload);
      return res;
    },
    async deleteServer({ commit }, { pk }) {
      const res = await axiosIns.delete(`servers/${pk}/`);
      return res;
    },
    async getCountries({ commit }, { pageNumber, name }) {
      let url = `countries/?page=${pageNumber}`;
      if (name) {
        url += `&name=${name}`;
      }
      const res = await axiosIns.get(url);
      return res;
    },
    async getCountry({ commit }, { pk }) {
      const res = await axiosIns.get(`countries/${pk}/`);
      return res;
    },
    async createCountry({ commit }, payload) {
      const res = await axiosIns.post("countries/", payload);
      return res;
    },
    async updateCountry({ commit }, { payload, pk }) {
      const res = await axiosIns.patch(`countries/${pk}/`, payload);
      return res;
    },
    async deleteCountry({ commit }, { pk }) {
      const res = await axiosIns.delete(`countries/${pk}/`);
      return res;
    },
  },
};
